import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import ChartDialog from './ChartDialog';

const EditSongModal = ({ song, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    album: '',
    duration: '',
    genre: '',
    bpm: '',
    key: '',
    year: '',
    status: 'GOOD',
    chart: ''
  });
  const [isChartDialogOpen, setIsChartDialogOpen] = useState(false);

  const parseDuration = (input) => {
    if (!input) return null;
    
    // Check if input is in MM:SS format
    if (input.includes(':')) {
      const parts = input.split(':');
      if (parts.length === 2) {
        const minutes = parseInt(parts[0]) || 0;
        const seconds = parseInt(parts[1]) || 0;
        return minutes * 60 + seconds;
      }
    }
    
    // Otherwise treat as seconds
    return parseInt(input) || null;
  };

  useEffect(() => {
    if (song) {
      setFormData({
        title: song.title || '',
        artist: song.artist || '',
        album: song.album || '',
        duration: song.duration ? song.duration.toString() : '',
        genre: song.genre || '',
        bpm: song.bpm ? song.bpm.toString() : '',
        key: song.key || '',
        year: song.year ? song.year.toString() : '',
        status: song.status || 'GOOD',
        chart: song.chart || ''
      });
    }
  }, [song]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const songData = {
      ...formData,
      duration: formData.duration ? parseDuration(formData.duration) : null,
      bpm: formData.bpm ? parseInt(formData.bpm) : null,
      year: formData.year ? parseInt(formData.year) : null,
      chart: formData.chart || null
    };
    onSave(songData);
  };

  const handleChartSave = (chart) => {
    setFormData(prev => ({
      ...prev,
      chart
    }));
  };

  const handleChartDelete = () => {
    setFormData(prev => ({
      ...prev,
      chart: ''
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Edit Song</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Artist *
            </label>
            <input
              type="text"
              name="artist"
              value={formData.artist}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Album
            </label>
            <input
              type="text"
              name="album"
              value={formData.album}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (seconds or MM:SS)
              </label>
              <input
                type="text"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                placeholder="180 or 3:00"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                BPM
              </label>
              <input
                type="number"
                name="bpm"
                value={formData.bpm}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Key
              </label>
              <input
                type="text"
                name="key"
                value={formData.key}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year
              </label>
              <input
                type="number"
                name="year"
                value={formData.year}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Genre
            </label>
            <input
              type="text"
              name="genre"
              value={formData.genre}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="GOOD">Good</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="TODO">To Do</option>
              <option value="BACKLOG">Backlog</option>
            </select>
          </div>

          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setIsChartDialogOpen(true)}
              className="w-full px-4 py-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center justify-center space-x-2 mb-4"
            >
              <DocumentTextIcon className="w-4 h-4" />
              <span>{formData.chart ? 'Edit Chart' : 'Add Chart'}</span>
            </button>
            {formData.chart && (
              <p className="text-sm text-gray-600 mb-4 text-center">
                Chart added ({formData.chart.length} characters)
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Update Song
            </button>
          </div>
        </form>
      </div>
      
      <ChartDialog
        isOpen={isChartDialogOpen}
        onClose={() => setIsChartDialogOpen(false)}
        onSave={handleChartSave}
        onDelete={handleChartDelete}
        initialChart={formData.chart}
      />
    </div>
  );
};

export default EditSongModal;